import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import FileUpload from '../../../components/FileUpload';
import ChoicesFormInput from '../../../components/form/ChoicesFormInput';
import IconifyIcon from '../../../components/IconifyIcon';
import { Card, CardBody, CardHeader, CardTitle, Col, Row, Button } from 'react-bootstrap';
import productRepository from '../../../services/repositories/product.repository';
import categoryRepository from '../../../services/repositories/category.repository';
import warehouseRepository from '../../../services/repositories/warehouse.repository';
import '../../../styles/products.scss';

const AddProduct = ({ isEdit = false, productId = null }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    brand: '',
    weight: '',
    description: '',
    sku: '',
    barcode: '',
    stock: '',
    stockAlert: '',
    warehouse_id: '', // Add warehouse_id field
    tags: [],
    price: '',
    price_casa: '',
    discount: '',
    tax: '',
    status: 'active', // Add status field
    mainImage: null, // Image principale (can be File or URL string)
    mainImageUrl: null, // URL of existing main image
    images: [], // Media gallery (can be Files or URL strings)
    existingGalleryImages: [], // Existing gallery images with ids [{ id, url }]
    hadMainImage: false, // Track if product initially had a main image
    disabled_fes: false,
    disabled_casa: false,
    has_variants: false,
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [categories, setCategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  // Fetch categories and warehouses on mount
  useEffect(() => {
    fetchCategories();
    fetchWarehouses();
  }, []);

  // Fetch product data when editing
  useEffect(() => {
    if (isEdit && productId) {
      console.log('Fetching product data for ID:', productId); // Debug log
      fetchProductData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, productId]);

  const fetchCategories = async () => {
    try {
      const response = await categoryRepository.getAll();
      const categoriesData = response.data || response;
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const response = await warehouseRepository.getAll();
      const warehousesData = response.data || response;
      setWarehouses(Array.isArray(warehousesData) ? warehousesData : []);
    } catch (err) {
      console.error('Error fetching warehouses:', err);
    }
  };

  const fetchProductData = async () => {
    try {
      setFetching(true);
      setError(null);
      
      const response = await productRepository.getById(productId);
      
      // Handle response structure - could be { data: {...} } or direct object
      const product = response.data || response;
      
      console.log('Fetched product data:', product); // Debug log
      
      if (!product || !product.id) {
        throw new Error('Product data not found');
      }
      
      // Import buildImageUrl helper
      const { buildImageUrl } = await import('../../../utils/imageUrl');
      
      // Get main image URL
      let mainImageUrl = null;
      if (product.main_image && product.main_image.image_path) {
        mainImageUrl = buildImageUrl(product.main_image.image_path);
      } else if (product.images && product.images.length > 0) {
        const mainImg = product.images.find(img => img.is_main) || product.images[0];
        if (mainImg && mainImg.image_path) {
          mainImageUrl = buildImageUrl(mainImg.image_path);
        }
      }
      
      // Get gallery images with ids and URLs
      const existingGalleryImages = [];
      if (product.gallery_images && product.gallery_images.length > 0) {
        product.gallery_images.forEach(img => {
          if (img.id && img.image_path) {
            existingGalleryImages.push({
              id: img.id,
              url: buildImageUrl(img.image_path)
            });
          }
        });
      }
      
      // Parse tags if they're a JSON string
      let tags = [];
      if (product.tags) {
        if (typeof product.tags === 'string') {
          try {
            tags = JSON.parse(product.tags);
          } catch (e) {
            tags = [];
          }
        } else if (Array.isArray(product.tags)) {
          tags = product.tags;
        }
      }
      
      setFormData({
        name: product.name || '',
        category_id: product.category_id || product.category?.id || '',
        brand: product.brand || '',
        weight: product.weight || '',
        description: product.description || '',
        sku: product.sku || '',
        barcode: product.barcode || '',
        stock: product.stock || '',
        stockAlert: product.stock_alert || '',
        warehouse_id: product.warehouse_id || product.warehouse?.id || '',
        tags: tags,
        price: product.price || '',
        price_casa: product.price_casa || '',
        discount: product.discount || '',
        tax: product.tax || '',
        status: product.status || 'active',
        mainImage: null, // New file upload
        mainImageUrl: mainImageUrl, // Existing image URL
        images: [], // New file uploads
        existingGalleryImages: existingGalleryImages, // Existing gallery images with ids
        hadMainImage: Boolean(mainImageUrl),
        disabled_fes: Boolean(product.disabled_fes),
        disabled_casa: Boolean(product.disabled_casa),
        has_variants: Boolean(product.has_variants),
      });
    } catch (err) {
      console.error('Error fetching product:', err);
      setError(err.message || 'Error loading product data');
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: nextValue }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Prepare data for API - include ALL fields from formData
      // Exclude only display-only fields in repository layer
      const submitData = {
        name: formData.name || '',
        category_id: formData.category_id || '',
        brand: formData.brand || '',
        weight: formData.weight || '',
        description: formData.description || '',
        sku: formData.sku || '',
        barcode: formData.barcode || '',
        stock: formData.stock || 0,
        stockAlert: formData.stockAlert || '',
        stock_alert: formData.stockAlert || '', // Also send as stock_alert for backend
        warehouse_id: formData.warehouse_id || '', // Include warehouse_id
        tags: Array.isArray(formData.tags) ? formData.tags : [],
        price: formData.price || 0,
        price_casa: formData.price_casa || 0,
        discount: formData.discount || 0,
        tax: formData.tax || 0,
        status: formData.status || 'active',
        disabled_fes: formData.disabled_fes ? 1 : 0,
        disabled_casa: formData.disabled_casa ? 1 : 0,
        has_variants: formData.has_variants ? 1 : 0,
        mainImage: formData.mainImage, // New file upload (can be null)
        images: Array.isArray(formData.images) ? formData.images : [], // New file uploads
        existingGalleryImageIds: Array.isArray(formData.existingGalleryImages)
          ? formData.existingGalleryImages.map((image) => image.id)
          : [],
        removeMainImage: isEdit && formData.hadMainImage && !formData.mainImage && !formData.mainImageUrl,
      };
      
      console.log('Submitting data:', { 
        ...submitData, 
        mainImage: submitData.mainImage ? 'File' : null, 
        imagesCount: submitData.images.length 
      }); // Debug log
      
      if (isEdit && productId) {
        // Update existing product
        console.log('Updating product ID:', productId);
        const result = await productRepository.update(productId, submitData);
        console.log('Update result:', result); // Debug log
      } else {
        // Create new product
        await productRepository.create(submitData);
      }
      
      // Navigate back to product list after success
      navigate('/admin/products/product-list');
    } catch (err) {
      console.error('Error saving product:', err);
      setError(err.response?.data?.message || err.message || 'Erreur lors de la sauvegarde du produit');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/products/product-list');
  };

  const handleMainImageChange = (files) => {
    if (files && files.length > 0) {
      setFormData(prev => ({ 
        ...prev, 
        mainImage: files[0],
        mainImageUrl: null // Clear existing URL when new file is uploaded
      }));
    }
  };

  const handleRemoveMainImage = () => {
    setFormData(prev => ({ 
      ...prev, 
      mainImage: null,
      mainImageUrl: null 
    }));
  };

  const handleMediaChange = (files) => {
    setFormData(prev => ({ ...prev, images: [...prev.images, ...files] }));
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleRemoveExistingGalleryImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      existingGalleryImages: prev.existingGalleryImages.filter((image) => image.id !== imageId)
    }));
  };

  if (fetching) {
    return (
      <Col xl={12} lg={12}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-3">Chargement des données du produit...</p>
        </div>
      </Col>
    );
  }

  return (
    <Col xl={12} lg={12}>
      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          {error}
        </div>
      )}
      {/* Image Principale */}
      <Card className="mb-3">
        <CardHeader>
          <CardTitle as={'h6'} className="mb-0" style={{ fontSize: '16px', fontWeight: '500' }}>Image Principale</CardTitle>
        </CardHeader>
        <CardBody>
          {formData.mainImage ? (
            <div className="text-center">
              <div className="position-relative d-inline-block">
                <img
                  src={formData.mainImage.preview || formData.mainImage}
                  alt="Image principale"
                  className="img-fluid rounded border"
                  style={{ maxWidth: '300px', maxHeight: '300px', objectFit: 'cover' }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  className="position-absolute top-0 end-0 m-2"
                  onClick={handleRemoveMainImage}
                  style={{ zIndex: 10 }}
                >
                  <IconifyIcon icon="solar:trash-bin-minimalistic-bold-duotone" />
                </Button>
                <div className="position-absolute bottom-0 start-0 m-2">
                  <span className="badge bg-primary">Image Principale (Nouvelle)</span>
                </div>
              </div>
            </div>
          ) : formData.mainImageUrl ? (
            <div className="text-center">
              <div className="position-relative d-inline-block">
                <img
                  src={formData.mainImageUrl}
                  alt="Image principale"
                  className="img-fluid rounded border"
                  style={{ maxWidth: '300px', maxHeight: '300px', objectFit: 'cover' }}
                />
                <Button
                  variant="danger"
                  size="sm"
                  className="position-absolute top-0 end-0 m-2"
                  onClick={handleRemoveMainImage}
                  style={{ zIndex: 10 }}
                >
                  <IconifyIcon icon="solar:trash-bin-minimalistic-bold-duotone" />
                </Button>
                <div className="position-absolute bottom-0 start-0 m-2">
                  <span className="badge bg-success">Image Principale (Existante)</span>
                </div>
              </div>
              <div className="mt-3">
                <FileUpload 
                  title="Remplacer l'image" 
                  onFilesChange={handleMainImageChange}
                  maxFiles={1}
                />
              </div>
            </div>
          ) : (
            <FileUpload 
              title="" 
              onFilesChange={handleMainImageChange}
              maxFiles={1}
            />
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle as={'h6'} className="mb-0" style={{ fontSize: '16px', fontWeight: '500' }}>Informations du Produit</CardTitle>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="product-name" className="form-label">
                    Nom du Produit
                  </label>
                  <input
                    type="text"
                    id="product-name"
                    name="name"
                    className="form-control"
                    placeholder="Nom du produit"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="product-categories" className="form-label">
                    Catégories du Produit
                  </label>
                  <select
                    className="form-control"
                    id="product-categories"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={12}>
                <div className="mb-4" style={{ paddingBottom: '12px' }}>
                  <label className="form-label">Disponibilité par région</label>
                  <Row className="g-4">
                    <Col lg={6}>
                      <label
                        htmlFor="product-disabled-fes"
                        className={`position-relative w-100 border rounded p-3 d-flex align-items-start gap-3 bg-light-subtle ${
                          formData.disabled_fes ? 'border-primary' : ''
                        }`}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <input
                          className="visually-hidden"
                          type="checkbox"
                          id="product-disabled-fes"
                          name="disabled_fes"
                          checked={Boolean(formData.disabled_fes)}
                          onChange={handleInputChange}
                          style={{
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            width: 0,
                            height: 0,
                            margin: 0,
                            padding: 0,
                            position: 'absolute',
                            left: '-9999px',
                          }}
                        />
                        <span
                          className={`d-inline-flex align-items-center justify-content-center border rounded flex-shrink-0 ${
                            formData.disabled_fes ? 'border-primary text-primary' : 'text-muted'
                          }`}
                          style={{
                            width: '18px',
                            height: '18px',
                            marginTop: '2px',
                            background: '#fff',
                          }}
                          aria-hidden="true"
                        >
                          {formData.disabled_fes ? <IconifyIcon icon="solar:check-circle-bold" /> : null}
                        </span>
                        <div>
                          <div style={{ fontWeight: 600 }}>Fès & alentours</div>
                          <div className="text-muted" style={{ fontSize: '13px', lineHeight: 1.35 }}>
                            {formData.disabled_fes
                              ? 'Désactivé pour les clients de Fès'
                              : 'Actif pour les clients de Fès'}
                          </div>
                        </div>
                      </label>
                    </Col>

                    <Col lg={6}>
                      <label
                        htmlFor="product-disabled-casa"
                        className={`position-relative w-100 border rounded p-3 d-flex align-items-start gap-3 bg-light-subtle ${
                          formData.disabled_casa ? 'border-primary' : ''
                        }`}
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        <input
                          className="visually-hidden"
                          type="checkbox"
                          id="product-disabled-casa"
                          name="disabled_casa"
                          checked={Boolean(formData.disabled_casa)}
                          onChange={handleInputChange}
                          style={{
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            width: 0,
                            height: 0,
                            margin: 0,
                            padding: 0,
                            position: 'absolute',
                            left: '-9999px',
                          }}
                        />
                        <span
                          className={`d-inline-flex align-items-center justify-content-center border rounded flex-shrink-0 ${
                            formData.disabled_casa ? 'border-primary text-primary' : 'text-muted'
                          }`}
                          style={{
                            width: '18px',
                            height: '18px',
                            marginTop: '2px',
                            background: '#fff',
                          }}
                          aria-hidden="true"
                        >
                          {formData.disabled_casa ? <IconifyIcon icon="solar:check-circle-bold" /> : null}
                        </span>
                        <div>
                          <div style={{ fontWeight: 600 }}>Casablanca & alentours</div>
                          <div className="text-muted" style={{ fontSize: '13px', lineHeight: 1.35 }}>
                            {formData.disabled_casa
                              ? 'Désactivé pour les clients de Casablanca'
                              : 'Actif pour les clients de Casablanca'}
                          </div>
                        </div>
                      </label>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={4}>
                <div className="mb-3">
                  <label htmlFor="product-brand" className="form-label">
                    Marque
                  </label>
                  <input
                    type="text"
                    id="product-brand"
                    name="brand"
                    className="form-control"
                    placeholder="Nom de la marque"
                    value={formData.brand}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
              <Col lg={4}>
                <div className="mb-3">
                  <label htmlFor="product-weight" className="form-label">
                    Poids / Volume
                  </label>
                  <input
                    type="text"
                    id="product-weight"
                    name="weight"
                    className="form-control"
                    placeholder="Ex: 500g, 1kg, 250ml"
                    value={formData.weight}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
              <Col lg={4}>
                <div className="mb-3">
                  <label htmlFor="product-sku" className="form-label">
                    SKU (Code Produit)
                  </label>
                  <input
                    type="text"
                    id="product-sku"
                    name="sku"
                    className="form-control"
                    placeholder="SKU-001"
                    value={formData.sku}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
            </Row>
            <Row>
              <Col lg={4}>
                <div className="mb-3">
                  <label htmlFor="product-barcode" className="form-label">
                    Code-barres
                  </label>
                  <input
                    type="text"
                    id="product-barcode"
                    name="barcode"
                    className="form-control"
                    placeholder="1234567890123"
                    value={formData.barcode}
                    onChange={handleInputChange}
                  />
                </div>
              </Col>
              <Col lg={4}>
                <div className="mb-3">
                  <label htmlFor="product-stock" className="form-label">
                    Stock (Quantité)
                  </label>
                  <input
                    type="number"
                    id="product-stock"
                    name="stock"
                    className="form-control"
                    placeholder="Quantité en stock"
                    value={formData.stock}
                    onChange={handleInputChange}
                    required
                    min="0"
                  />
                </div>
              </Col>
              <Col lg={4}>
                <div className="mb-3">
                  <label htmlFor="product-stock-alert" className="form-label">
                    Seuil d'Alerte Stock
                  </label>
                  <input
                    type="number"
                    id="product-stock-alert"
                    name="stockAlert"
                    className="form-control"
                    placeholder="Alerte quand stock <"
                    value={formData.stockAlert}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </Col>
            </Row>

            <Row className="align-items-end">
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="product-warehouse" className="form-label">
                    Entrepôt
                  </label>
                  <select
                    className="form-control"
                    id="product-warehouse"
                    name="warehouse_id"
                    value={formData.warehouse_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Sélectionner un entrepôt</option>
                    {warehouses.map((warehouse) => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
              <Col lg={6}>
                <div className="mb-3 p-2 border border-dashed border-primary rounded bg-primary-subtle d-flex flex-column justify-content-center" style={{ height: '70px' }}>
                  <div className="form-check form-switch mb-0">
                    <input 
                      className="form-check-input" 
                      type="checkbox" 
                      id="has_variants" 
                      name="has_variants" 
                      checked={formData.has_variants}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label fw-bold text-primary" htmlFor="has_variants">
                      ✨ Activer les Variantes de Poids
                    </label>
                  </div>
                  <small className="text-muted" style={{ fontSize: '11px' }}>
                    Permettre le choix du poids (250g, 500g, 1kg...).
                  </small>
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={12}>
                <div className="mb-3">
                  <label htmlFor="description" className="form-label">
                    Description du Produit
                  </label>
                  <div className="description-editor-container" style={{ minHeight: '450px' }}>
                    <ReactQuill
                      theme="snow"
                      value={formData.description}
                      onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                      placeholder="Rédigez une description détaillée du produit..."
                      modules={{
                        toolbar: [
                          [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                          [{ 'indent': '-1'}, { 'indent': '+1' }],
                          [{ 'color': [] }, { 'background': [] }],
                          [{ 'align': [] }],
                          ['link'],
                          ['clean']
                        ],
                      }}
                      formats={[
                        'header',
                        'bold', 'italic', 'underline', 'strike',
                        'list', 'bullet', 'indent',
                        'color', 'background',
                        'align',
                        'link'
                      ]}
                      style={{ height: '400px', backgroundColor: 'white' }}
                    />
                  </div>
                  <small className="text-muted mt-2 d-block">
                    Utilisez les outils de mise en forme pour créer une description attrayante avec des titres, listes, et styles.
                  </small>
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={6}>
                <div className="mb-3">
                  <label htmlFor="product-tags" className="form-label">
                    Tags
                  </label>
                  <select
                    className="form-control"
                    id="product-tags"
                    name="tags"
                    multiple
                    value={formData.tags}
                    onChange={(e) => {
                      const selected = Array.from(e.target.selectedOptions, option => option.value);
                      setFormData(prev => ({ ...prev, tags: selected }));
                    }}
                  >
                    <option value="bio">Bio</option>
                    <option value="premium">Premium</option>
                    <option value="local">Local</option>
                    <option value="imported">Importé</option>
                    <option value="organic">Organique</option>
                    <option value="new">Nouveau</option>
                    <option value="promotion">Promotion</option>
                  </select>
                  <small className="text-muted">Maintenez Ctrl (Cmd sur Mac) pour sélectionner plusieurs tags</small>
                </div>
              </Col>
            </Row>
          </form>
        </CardBody>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle as={'h6'} className="mb-0" style={{ fontSize: '16px', fontWeight: '500' }}>Détails de Prix</CardTitle>
        </CardHeader>
        <CardBody>
          <Row>
            <Col lg={4}>
              <div className="mb-3">
                <label htmlFor="product-price" className="form-label">
                  Prix Normal (Fes)
                </label>
                <div className="input-group mb-3">
                  <span className="input-group-text fs-20">
                    <IconifyIcon icon="solar:dollar-bold-duotone" />
                  </span>
                  <input
                    type="number"
                    id="product-price"
                    name="price"
                    className="form-control"
                    placeholder="0.00"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
            </Col>
            <Col lg={4}>
              <div className="mb-3">
                <label htmlFor="product-price-casa" className="form-label">
                  Prix Casa
                </label>
                <div className="input-group mb-3">
                  <span className="input-group-text fs-20">
                    <IconifyIcon icon="solar:dollar-bold-duotone" />
                  </span>
                  <input
                    type="number"
                    id="product-price-casa"
                    name="price_casa"
                    className="form-control"
                    placeholder="0.00"
                    value={formData.price_casa}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </Col>
            <Col lg={4}>
              <div className="mb-3">
                <label htmlFor="product-discount" className="form-label">
                  Remise
                </label>
                <div className="input-group mb-3">
                  <span className="input-group-text fs-20">
                    <IconifyIcon icon="solar:tag-price-bold-duotone" />
                  </span>
                  <input
                    type="number"
                    id="product-discount"
                    name="discount"
                    className="form-control"
                    placeholder="0"
                    value={formData.discount}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </Col>
            <Col lg={4}>
              <div className="mb-3">
                <label htmlFor="product-tax" className="form-label">
                  Taxe
                </label>
                <div className="input-group mb-3">
                  <span className="input-group-text fs-20">
                    <IconifyIcon icon="solar:document-text-bold-duotone" />
                  </span>
                  <input
                    type="number"
                    id="product-tax"
                    name="tax"
                    className="form-control"
                    placeholder="0"
                    value={formData.tax}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Media Gallery */}
      <Card className="mb-3">
        <CardHeader>
          <CardTitle as={'h6'} className="mb-0" style={{ fontSize: '16px', fontWeight: '500' }}>Galerie Médias</CardTitle>
        </CardHeader>
        <CardBody>
          <FileUpload 
            title="Ajouter des Images à la Galerie" 
            onFilesChange={handleMediaChange}
          />
          {(formData.images.length > 0 || formData.existingGalleryImages.length > 0) && (
            <div className="mt-4">
              <h5 className="mb-3">
                Images de la Galerie ({formData.images.length + formData.existingGalleryImages.length})
              </h5>
              <Row className="g-3">
                {/* Existing images */}
                {formData.existingGalleryImages.map((image, index) => (
                  <Col key={`existing-${image.id}`} xs={6} sm={4} md={3} lg={2}>
                    <div className="position-relative">
                      <img
                        src={image.url}
                        alt={`Gallery ${index + 1}`}
                        className="img-fluid rounded border"
                        style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 m-1"
                        onClick={() => handleRemoveExistingGalleryImage(image.id)}
                        style={{ zIndex: 10 }}
                      >
                        <IconifyIcon icon="solar:trash-bin-minimalistic-bold-duotone" />
                      </Button>
                      <div className="position-absolute bottom-0 start-0 m-1">
                        <span className="badge bg-success">Existante</span>
                      </div>
                    </div>
                  </Col>
                ))}
                {/* New images */}
                {formData.images.map((image, index) => (
                  <Col key={`file-${index}`} xs={6} sm={4} md={3} lg={2}>
                    <div className="position-relative">
                      <img
                        src={image.preview || image}
                        alt={`Gallery ${formData.existingGalleryImages.length + index + 1}`}
                        className="img-fluid rounded border"
                        style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 m-1"
                        onClick={() => handleRemoveImage(index)}
                        style={{ zIndex: 10 }}
                      >
                        <IconifyIcon icon="solar:trash-bin-minimalistic-bold-duotone" />
                      </Button>
                      <div className="position-absolute bottom-0 start-0 m-1">
                        <span className="badge bg-primary">Nouvelle</span>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </CardBody>
      </Card>

      <div className="p-3 bg-light mb-3 rounded">
        <Row className="justify-content-end g-2">
          <Col lg={2}>
            <Button variant="dark" className="w-100" onClick={handleCancel}>
              Annuler
            </Button>
          </Col>
          <Col lg={2}>
            <Button 
              variant="primary" 
              className="w-100" 
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Enregistrement...' : (isEdit ? 'Enregistrer' : 'Créer le Produit')}
            </Button>
          </Col>
        </Row>
        {error && (
          <Row className="mt-2">
            <Col>
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            </Col>
          </Row>
        )}
      </div>
    </Col>
  );
};

export default AddProduct;
